import { useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeTemplate, MEDIA } from '@cloudsign/shared';
import type { BadgeElement, BadgeLayout } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Button, Card, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { LabelCanvas, elementName } from './LabelCanvas';
import { Properties } from './Properties';
import type { FieldChoice } from './Properties';
import { DATA_KEYS, PALETTE, addElement, makeElement, outsideIds, removeElement, sameLayout, updateElement } from './layout';
import type { PaletteKind } from './layout';
import { fetchPreviewPdf, templateKey, useSignInFields, useTemplate } from './api';

const MEDIA_KEYS = Object.keys(MEDIA) as BadgeLayout['media'][];

function mediaLabel(media: BadgeLayout['media']): string {
  return t(`badgeUi.media.${media}`);
}

function Designer({ template }: { template: BadgeTemplate }) {
  const qc = useQueryClient();
  const push = useToast();
  const canEdit = useCan('badges.manage');
  const [layout, setLayout] = useState<BadgeLayout>(template.layout);
  const [name, setName] = useState(template.name);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fields = useSignInFields(template.siteId);
  const logoInput = useRef<HTMLInputElement>(null);

  const choices: FieldChoice[] = useMemo(
    () => (fields.data ?? []).map((f) => ({ key: `field:${f.id}`, label: f.label, starred: f.starred })),
    [fields.data],
  );
  const fieldLabels = useMemo(() => Object.fromEntries(choices.map((c) => [c.key, c.label])), [choices]);
  const values = useMemo(() => {
    const v: Record<string, string> = {};
    for (const k of DATA_KEYS) v[k] = t(`badgeUi.sample.${k}`);
    for (const c of choices) v[c.key] = `${t('badgeUi.sample.field')} ${c.label}`;
    for (const el of layout.elements) {
      if (el.type === 'text' && el.source.kind === 'data' && !(el.source.key in v)) v[el.source.key] = t('badgeUi.sample.field');
    }
    return v;
  }, [choices, layout.elements]);

  const outside = useMemo(() => new Set(outsideIds(layout)), [layout]);
  const selected = layout.elements.find((e) => e.id === selectedId) ?? null;
  const dirty = !sameLayout(layout, template.layout) || name.trim() !== template.name;

  const save = useMutation({
    mutationFn: () => api.patch(`/badges/templates/${template.id}`, { body: { name: name.trim(), layout }, schema: BadgeTemplate }),
    onSuccess: async (saved) => {
      qc.setQueryData(templateKey(saved.id), saved);
      await qc.invalidateQueries({ queryKey: ['badges', 'templates'] });
      push(t('badgeUi.saved', { version: saved.version }));
    },
  });

  const preview = useMutation({
    mutationFn: async () => {
      // Open the tab now (a click is needed to avoid pop-up blockers), then point it at the PDF.
      const tab = window.open('', '_blank');
      try {
        const blob = await fetchPreviewPdf({ layout, templateId: template.id, sample: values });
        const url = URL.createObjectURL(blob);
        if (tab) tab.location.href = url;
        else window.open(url, '_blank');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        tab?.close();
        throw err;
      }
    },
  });

  const logo = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(`/badges/templates/${template.id}/logo`, { form, schema: BadgeTemplate });
    },
    onSuccess: (saved) => {
      qc.setQueryData(templateKey(saved.id), saved);
      push(t('badgeUi.logo.uploaded'));
    },
  });

  const add = (kind: PaletteKind) => {
    const el = makeElement(kind, layout, t('badgeUi.create.starterText'));
    setLayout(addElement(layout, el));
    setSelectedId(el.id);
  };
  const patch = (p: Partial<BadgeElement>) => selected && setLayout(updateElement(layout, selected.id, p));
  const remove = () => {
    if (!selected) return;
    setLayout(removeElement(layout, selected.id));
    setSelectedId(null);
  };
  const pickLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) logo.mutate(file);
    e.target.value = '';
  };

  const outsideNames = layout.elements.filter((e) => outside.has(e.id)).map((e) => elementName(e, fieldLabels));
  const err = save.error ?? preview.error ?? logo.error;

  return (
    <div>
      <p className="mb-2 text-sm">
        <Link to="/manage/badges" className="text-brand underline">
          {t('badgeUi.designer.back')}
        </Link>
      </p>
      <PageHeader
        title={template.name}
        description={t('badgeUi.version', { version: template.version })}
        actions={
          <>
            {canEdit ? (
              <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {t('badgeUi.save')}
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                disabled={!dirty}
                onClick={() => {
                  setLayout(template.layout);
                  setName(template.name);
                  setSelectedId(null);
                }}
              >
                {t('badgeUi.discard')}
              </Button>
            ) : null}
            {canEdit ? (
              <Button disabled={preview.isPending} onClick={() => preview.mutate()}>
                {t('badgeUi.preview')}
              </Button>
            ) : null}
          </>
        }
      />
      {!canEdit ? <p className="mb-3 text-sm text-slate-700">{t('badgeUi.designer.readOnly')}</p> : null}
      {err ? <ErrorLine error={err} /> : null}
      {outsideNames.length > 0 ? (
        <div role="status" className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t('badgeUi.warn.outside', { names: outsideNames.join(', ') })}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <Card>
            <div className="grid gap-3 sm:grid-cols-3">
              <TextField label={t('badgeUi.field.name')} value={name} maxLength={80} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
              <SelectField
                label={t('badgeUi.field.media')}
                value={layout.media}
                disabled={!canEdit}
                onChange={(e) => setLayout({ ...layout, media: e.target.value as BadgeLayout['media'] })}
              >
                {MEDIA_KEYS.map((m) => (
                  <option key={m} value={m}>
                    {mediaLabel(m)}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label={t('badgeUi.field.orientation')}
                value={layout.orientation}
                disabled={!canEdit}
                onChange={(e) => setLayout({ ...layout, orientation: e.target.value as BadgeLayout['orientation'] })}
              >
                <option value="landscape">{t('badgeUi.orientation.landscape')}</option>
                <option value="portrait">{t('badgeUi.orientation.portrait')}</option>
              </SelectField>
            </div>
          </Card>
          <Card description={canEdit ? t('badgeUi.designer.canvasHelp') : undefined}>
            <div className="mx-auto" style={{ maxWidth: '40rem' }}>
              <LabelCanvas
                layout={layout}
                values={values}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onChange={setLayout}
                canEdit={canEdit}
                outside={outside}
                fieldLabels={fieldLabels}
              />
            </div>
            <p role="status" aria-live="polite" className="mt-2 min-h-5 text-xs text-slate-700">
              {selected ? t('badgeUi.position', { name: elementName(selected, fieldLabels), x: selected.x, y: selected.y, w: selected.w, h: selected.h }) : ''}
            </p>
          </Card>
        </div>

        <div className="space-y-4">
          {canEdit ? (
            <Card title={t('badgeUi.designer.palette')}>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((kind) => (
                  <Button key={kind} onClick={() => add(kind)} disabled={layout.elements.length >= 30}>
                    {t(`badgeUi.add.${kind}`)}
                  </Button>
                ))}
              </div>
              <div className="mt-3">
                <input ref={logoInput} type="file" accept="image/png,image/jpeg" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={pickLogo} />
                <Button onClick={() => logoInput.current?.click()} disabled={logo.isPending}>
                  {t('badgeUi.logo.upload')}
                </Button>
              </div>
            </Card>
          ) : null}
          <Card title={t('badgeUi.designer.properties')}>
            {selected ? (
              <Properties element={selected} fields={choices} canEdit={canEdit} onPatch={patch} onRemove={remove} />
            ) : (
              <p className="text-sm text-slate-700">{t('badgeUi.designer.noSelection')}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export function DesignerPage() {
  const { id } = useParams();
  const q = useTemplate(id);
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  return <Designer key={q.data.id} template={q.data} />;
}
